import {
  OrganizationSwitcher,
  SignedIn,
  SignedOut,
  UserButton,
  useSessionTokenGetter,
} from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

const PRO_MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_PRO ?? '';
const PRO_ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID_PRO_ANNUAL ?? '';

interface MeResponse {
  needsSubscription: boolean;
  planTier: string | null;
}

const TRIAL_FEATURES = [
  '2 full impact analyses (unlimited once your subscription starts)',
  'Community Health, Workforce, Industry, and Wage intelligence',
  'Branded PDF exports with AI summaries',
  'Custom RIMS II data import',
  'Project intake forms',
];

/**
 * Card-required reverse trial wall. A signed-in owner whose org has not yet
 * started a subscription is routed here (by ProtectedShell) before any app
 * access. They enter a card via Stripe Checkout; the subscription runs a
 * 14-day trial, then auto-charges unless they cancel. Once the Stripe webhook
 * flips the org to a paid tier, /me reports needsSubscription=false and this
 * route bounces them on to the app.
 */
function StartTrialWall() {
  const getToken = useSessionTokenGetter();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // After returning from Stripe Checkout (?started=1) the subscription may
    // not be webhook-recorded yet; poll /me a few times so the user isn't
    // bounced or tempted into a second checkout while it settles.
    const justStarted = new URLSearchParams(window.location.search).get('started') === '1';
    let attempts = 0;
    const check = async () => {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (cancelled) return;
      if (res.ok) {
        const data = (await res.json()) as MeResponse;
        if (cancelled) return;
        setMe(data);
        // Webhook not landed yet — keep the loader up and poll again.
        if (justStarted && data.needsSubscription && attempts < 6) {
          attempts += 1;
          setTimeout(check, 2000);
          return;
        }
      }
      setLoading(false);
    };
    void check();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const startTrial = async () => {
    const priceId = annual ? PRO_ANNUAL_PRICE_ID : PRO_MONTHLY_PRICE_ID;
    if (!priceId) {
      setError('Billing is not configured. Please contact support.');
      return;
    }
    setCheckoutLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/billing/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          // Return to the wall with a flag so we can poll until the webhook has
          // recorded the subscription, then forward to the dashboard. Going
          // straight to /dashboard risks the gate bouncing them back here (and
          // a duplicate checkout) before the webhook lands.
          successUrl: `${window.location.origin}/start-trial?started=1`,
          cancelUrl: `${window.location.origin}/start-trial`,
        }),
      });
      if (res.ok) {
        const { url } = (await res.json()) as { url: string };
        window.location.href = url;
        return;
      }
      if (res.status === 403) {
        setError(
          'Only the organization owner can start the subscription. Ask your owner to set it up.',
        );
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch {
      setError('Could not start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  // Already subscribed (or comped) — don't sit on the wall.
  if (me && !me.needsSubscription) {
    return <Navigate to="/dashboard" replace />;
  }

  const billingLabel = annual ? '$2,990' : '$299';
  const renewalCadence = annual ? 'every year' : 'every month';
  const trialEndLabel = new Date(Date.now() + 14 * 86_400_000).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-brand-teal-50/40">
      {/* Escape hatch: the org is walled, but the user must never be trapped
          with only the payment button. Let them switch to another org they
          belong to (which re-runs the gate) or sign out entirely. */}
      <header className="flex items-center justify-between px-6 py-4">
        <OrganizationSwitcher
          hidePersonal
          afterSelectOrganizationUrl="/dashboard"
          afterCreateOrganizationUrl="/start-trial"
        />
        <UserButton afterSignOutUrl="/sign-in" />
      </header>
      <div className="flex flex-1 items-center justify-center px-4 pb-10">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Start your 14-day free trial
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Full Professional access for 14 days. You will not be charged until the trial ends,
              and you can cancel anytime before then.
            </p>
          </div>

          <ul className="mb-6 space-y-2">
            {TRIAL_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-0.5 text-brand-teal-600">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mb-5 flex items-center justify-center gap-3 text-sm">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={`rounded-md px-3 py-1.5 font-medium ${
                !annual ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              Monthly · $299/mo
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={`rounded-md px-3 py-1.5 font-medium ${
                annual ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              Annual · $2,990/yr (save $598)
            </button>
          </div>

          {error && (
            <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-700">
              {error}
            </p>
          )}

          {/* Recurring billing disclosure — required so the user clearly consents
            to the auto-charge at trial end and the recurring charges after. */}
          <p className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500">
            <strong className="text-slate-600">No charge today.</strong> When your 14-day free trial
            ends on <strong className="text-slate-600">{trialEndLabel}</strong>, your card will be
            charged <strong className="text-slate-600">{billingLabel}</strong>, and it will
            automatically renew at <strong className="text-slate-600">{billingLabel}</strong>{' '}
            {renewalCadence} until you cancel. Cancel anytime before the trial ends in Settings →
            Billing to avoid any charge.
          </p>

          <button
            type="button"
            onClick={startTrial}
            disabled={checkoutLoading}
            className="w-full rounded-lg bg-brand-teal-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {checkoutLoading ? 'Redirecting to checkout…' : 'Start free trial'}
          </button>
          <p className="mt-3 text-center text-xs text-slate-400">
            By starting your trial you authorize this recurring charge. Card required, secured by
            Stripe. Cancel anytime in Settings → Billing.
          </p>
        </div>
      </div>
    </div>
  );
}

export function StartTrialRoute() {
  return (
    <>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
      <SignedIn>
        <StartTrialWall />
      </SignedIn>
    </>
  );
}
